import { Injectable, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';

import * as AccountActions from './account.actions';
// import * as AccountFeature from './account.reducer';
import * as AccountSelectors from './account.selectors';
import { Observable, take } from 'rxjs';
import { IGetAccountReportExRequest, TError } from '@amwager/util-ts';

@Injectable()
export class AccountFacade {
  private readonly store = inject(Store);

  // Selectors — expose store state as observables
  isError$(): Observable<TError> {
    return this.store.pipe(select(AccountSelectors.getAccountError));
  }

  getBalance$(): Observable<number | undefined> {
    return this.store.pipe(select(AccountSelectors.getBalance));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isGetUserAccountSuccess$(): Observable<any> {
    return this.store.pipe(select(AccountSelectors.isGetUserAccountSuccess))
  }

  getAccountStatements$() {
    return this.store.pipe(select(AccountSelectors.getAccountStatements));
  }

  getBetSummary$() {
    return this.store.pipe(select(AccountSelectors.getBetSummary));
  }

  getOverview$() {
    return this.store.pipe(select(AccountSelectors.getOverview));
  }

  isSelfExcludeOrSuspendSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccess: boolean | undefined,
    isProcessing: boolean | undefined
  }> {
    return this.store.pipe(select(AccountSelectors.isSelfExcludeOrSuspendSuccess));
  }

  isSelfExcludeOrSuspendVerificationSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccess: boolean | undefined,
    isProcessing: boolean | undefined
  }> {
    return this.store.pipe(select(AccountSelectors.isSelfExcludeOrSuspendVerificationSuccess));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isGetAccountPreferencesSuccess$(): Observable<any> {
    return this.store.pipe(select(AccountSelectors.isGetAccountPreferencesSuccess))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isUpsertAccountPrefenceFieldSuccess$(): Observable<any> {
    return this.store.pipe(select(AccountSelectors.isUpsertAccountPrefenceFieldSuccess))
  }

  getPreferenceFavorites$(): Observable<string[] | undefined> {
    return this.store.pipe(select(AccountSelectors.getPreferenceFavorites))
  }

  isGetAccountDailyLossLimitSuccess$(): Observable<any> {
    return this.store.pipe(select(AccountSelectors.isGetAccountDailyLossLimitSuccess))
  }


  /**
   * Use the initialization action to perform one
   * or more tasks in your Effects.
   *
   * Dispatches: Requests only, from WagerActionss
   */


  /**
   * Initialize the account state (idempotent — do not call multiple times)
   * @param {string} sourceInitiator Component that triggered init
   * @returns {void}
   */
  initState(sourceInitiator: string): void {
    this.store.select(AccountSelectors.getAccountLoaded)
      .pipe(take(1))
      .subscribe(isLoaded => {
        isLoaded
          ? null
          : this.store.dispatch(AccountActions.initAccountState({ sourceInitiator }))
      });
  }
  clearState(sourceInitiator: string): void {
    this.store.dispatch(AccountActions.clearAccountState({ sourceInitiator }));
  }

  setError(error: TError, sourceInitiator: string): void {
    this.store.dispatch(AccountActions.setError({ error, sourceInitiator }));
  }

  getBalance(sourceInitiator: string,): void {
    this.store.dispatch(AccountActions.getAccountBalanceAPI({
      sourceInitiator
    }));
  }

  setBalance(balance: number, sourceInitiator: string,): void {
    this.store.dispatch(AccountActions.setBalance({
      sourceInitiator,
      balance
    }));
  }

  // account section
  getUserAccoutAPI(): void {
    this.store.dispatch(AccountActions.getUserAccountAPI());
  }

  // eprotect information section
  getUserEprotectInformationAPI(): void {
    this.store.dispatch(AccountActions.getUserEprotectInformationAPI());
  }

  getAccountStatements(sourceInitiator: string, requests: IGetAccountReportExRequest): void {
    this.store.dispatch(AccountActions.getAccountStatementsAPI({
      sourceInitiator,
      requests
    }));
  }

  getBetSummary(sourceInitiator: string, requests: IGetAccountReportExRequest): void {
    this.store.dispatch(AccountActions.getBetSummaryAPI({
      sourceInitiator,
      requests
    }));
  }

  getOverview(sourceInitiator: string, requests: IGetAccountReportExRequest): void {
    this.store.dispatch(AccountActions.getOverviewAPI({
      sourceInitiator,
      requests
    }));
  }

  // responsible section
  selfExcludeOrSuspend(days: number, years: number, isExclusion: boolean): void {
    this.store.dispatch(AccountActions.selfExcludeOrSuspendAPI({
      days,
      years,
      isExclusion
    }));
  }

  selfExcludeOrSuspendVerification(verificationToken: string): void {
    this.store.dispatch(AccountActions.selfExcludeOrSuspendVerificationAPI({
      verificationToken
    }));
  }

  // wagering preferences
  getAccountPreferencesAPI(): void {
    this.store.dispatch(AccountActions.getAccountPreferencesAPI());
  }

  upsertAccountPrefenceFieldAPI(FieldName: string, Value: string): void {
    this.store.dispatch(AccountActions.upsertAccountPrefenceFieldAPI({FieldName, Value}));
  }

  // wagering daily loss limit
  getAccountDailyLossLimitAPI(): void {
    this.store.dispatch(AccountActions.getAccountDailyLossLimitAPI());
  }

  updatetAccountDailyLossLimitAPI(amount: number): void {
    this.store.dispatch(AccountActions.updateAccountDailyLossLimitAPI({amount}));
  }

  getPromoListAPI(): void {
    this.store.dispatch(AccountActions.getPromoListAPI());
  }

  getPromoList$(): Observable<any> {
    return this.store.pipe(select(AccountSelectors.getPromoList));
  }

  useGiveAwayAPI(giveAwayName: string): void {
    this.store.dispatch(AccountActions.useGiveAwayAPI({giveAwayName}));
  }

  usePromoAPI(promoName: string): void {
    this.store.dispatch(AccountActions.usePromoAPI({promoName}));
  }

  useGiveAway$(): Observable<any> {
    return this.store.pipe(select(AccountSelectors.useGiveAway));
  }

  usePromo$(): Observable<any> {
    return this.store.pipe(select(AccountSelectors.usePromo));
  }

  clearGiveAway(): void {
    this.store.dispatch(AccountActions.clearGiveAway());
  }

  clearPromo(): void {
    this.store.dispatch(AccountActions.clearPromo());
  }
}
